import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Unsubscribe
} from "firebase/firestore";
import type { AssetDynamicField, DepreciationMethod } from "../types/imobi";
import { db } from "./firebase";

export type ReferenceCollection = "brands" | "departments";

export type ReferenceOption = {
  id: string;
  name: string;
};

export type AssetTypeOption = ReferenceOption & {
  usefulLifeYears: number | null;
  annualRate: number;
  depreciationMethod?: DepreciationMethod;
  dynamicFields?: AssetDynamicField[];
};

function normalizeReferenceId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function mapReference(id: string, data: Record<string, unknown>): ReferenceOption {
  return {
    id,
    name: String(data.name ?? data.label ?? id)
  };
}

function mapAssetType(id: string, data: Record<string, unknown>): AssetTypeOption {
  return {
    id,
    name: String(data.assetTypeName ?? data.name ?? id),
    usefulLifeYears: typeof data.usefulLifeYears === "number" ? data.usefulLifeYears : null,
    annualRate: typeof data.annualRate === "number" ? data.annualRate : 0,
    depreciationMethod: data.depreciationMethod as DepreciationMethod | undefined,
    dynamicFields: Array.isArray(data.dynamicFields) ? data.dynamicFields as AssetDynamicField[] : []
  };
}

export function subscribeGlobalReferences(collectionName: ReferenceCollection, onChange: (items: ReferenceOption[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, collectionName), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapReference(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeCompanyLocations(companyId: string, onChange: (items: ReferenceOption[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "locations"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapReference(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeCompanyBrands(companyId: string, onChange: (items: ReferenceOption[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "brands"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapReference(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function saveCompanyBrand(companyId: string, name: string): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const trimmedName = name.trim().toLocaleUpperCase("pt-BR");
  const brandId = normalizeReferenceId(trimmedName);

  if (!companyId || !brandId) {
    return;
  }

  await setDoc(doc(db, "companies", companyId, "brands", brandId), {
    name: trimmedName,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export function subscribeAssetTypes(onChange: (items: AssetTypeOption[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "depreciationRules"), orderBy("assetTypeName")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapAssetType(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function saveGlobalReference(collectionName: ReferenceCollection, name: string): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const trimmedName = name.trim().toLocaleUpperCase("pt-BR");
  const referenceId = normalizeReferenceId(trimmedName);

  if (!referenceId) {
    return;
  }

  await setDoc(doc(db, collectionName, referenceId), {
    name: trimmedName,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function saveCompanyLocation(companyId: string, name: string): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const trimmedName = name.trim().toLocaleUpperCase("pt-BR");
  const locationId = normalizeReferenceId(trimmedName);

  if (!companyId || !locationId) {
    return;
  }

  await setDoc(doc(db, "companies", companyId, "locations", locationId), {
    name: trimmedName,
    updatedAt: serverTimestamp()
  }, { merge: true });
}
